import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Loader2, Users, Kanban, Building2, Check, X, Inbox } from "lucide-react";
import { adminApi, clearAdminToken } from "@/lib/adminApi";
import { LOGO_URL } from "@/lib/content";

const TABS = [
  { id: "pending", label: "Pending" },
  { id: "approved", label: "Approved" },
  { id: "rejected", label: "Rejected" },
];

export default function AdminApplications() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [tab, setTab] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/admin/venue-applications", {
        params: { status: tab },
      });
      setRows(data);
    } catch {
      toast.error("Failed to load applications");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, [tab]);

  const approve = async (id) => {
    if (!window.confirm("Approve this venue? An invite email will be sent immediately.")) return;
    setBusy(id);
    try {
      await adminApi.post(`/admin/venue-applications/${id}/approve`);
      toast.success("Approved — invite email sent.");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Approve failed");
    } finally {
      setBusy(null);
    }
  };

  const reject = async (id) => {
    if (!window.confirm("Reject this application?")) return;
    setBusy(id);
    try {
      await adminApi.post(`/admin/venue-applications/${id}/reject`);
      toast.success("Rejected");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reject failed");
    } finally {
      setBusy(null);
    }
  };

  const logout = () => {
    clearAdminToken();
    nav("/admin/login", { replace: true });
  };

  return (
    <div data-testid="admin-applications-page" className="min-h-screen bg-black text-[#F5F5F0]">
      <header className="border-b border-white/10 sticky top-0 z-30 bg-black/85 backdrop-blur-xl">
        <div className="max-w-[1600px] mx-auto px-6 md:px-8 flex items-center justify-between h-20 gap-4">
          <div className="flex items-center gap-6 min-w-0">
            <Link to="/admin">
              <img src={LOGO_URL} alt="Swingle" className="h-12 w-auto object-contain" />
            </Link>
            <nav className="hidden md:flex items-center gap-1">
              <Link to="/admin" className="inline-flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-[0.24em] text-[#A3A3A3] hover:text-[#D9A6F5]"><Kanban size={14} /> Pipeline</Link>
              <Link to="/admin/venues" className="inline-flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-[0.24em] text-[#A3A3A3] hover:text-[#D9A6F5]"><Building2 size={14} /> Venues</Link>
              <Link to="/admin/applications" className="inline-flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-[0.24em] text-[#D9A6F5]"><Inbox size={14} /> Applications</Link>
              <Link to="/admin/contacts" className="inline-flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-[0.24em] text-[#A3A3A3] hover:text-[#D9A6F5]"><Users size={14} /> Contacts</Link>
            </nav>
          </div>
          <button type="button" onClick={logout} className="inline-flex items-center gap-2 text-[#A3A3A3] hover:text-[#D9A6F5] px-3 py-2 text-xs uppercase tracking-[0.2em]">
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-6 md:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#D9A6F5] mb-1">Applications</div>
            <h1 className="text-2xl md:text-3xl font-black tracking-tighter">Venue sign-ups</h1>
          </div>
          <div className="flex items-center gap-1 border border-white/10 p-0.5">
            {TABS.map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`applications-tab-${t.id}`}
                className={`px-4 py-2 text-[11px] uppercase tracking-[0.22em] transition-colors ${
                  tab === t.id ? "bg-[#D9A6F5] text-black" : "text-[#A3A3A3] hover:text-[#F5F5F0]"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="border border-white/10 p-16 flex items-center justify-center text-[#A3A3A3]">
            <Loader2 size={16} className="animate-spin mr-3" /> Loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="border border-white/10 p-16 text-center text-[#A3A3A3] text-sm">
            No {tab} applications.
          </div>
        ) : (
          <div className="space-y-3">
            {rows.map((r) => (
              <div key={r.id} data-testid={`application-${r.id}`} className="border border-white/10 bg-white/[0.02]">
                <div className="p-5 flex items-start justify-between gap-6">
                  <div className="min-w-0">
                    <div className="text-lg font-bold text-[#F5F5F0]">{r.venue_name}</div>
                    <div className="text-xs text-[#A3A3A3] mt-1">
                      {r.contact_name} · {r.contact_email} · {r.contact_phone}
                    </div>
                    <div className="text-xs text-[#A3A3A3] mt-1">
                      {r.address_line1}
                      {r.address_line2 ? `, ${r.address_line2}` : ""}, {r.city} {r.postcode}, {r.country}
                    </div>
                    <button
                      type="button"
                      onClick={() => setExpanded(expanded === r.id ? null : r.id)}
                      className="mt-3 text-[10px] uppercase tracking-[0.22em] text-[#D9A6F5] hover:underline"
                    >
                      {expanded === r.id ? "Hide details" : "Show details"}
                    </button>
                    {expanded === r.id && (
                      <div className="mt-3 space-y-1 text-xs text-[#C9C9C0]">
                        {r.website && <div>Website: <a href={r.website} target="_blank" rel="noreferrer" className="text-[#D9A6F5]">{r.website}</a></div>}
                        {r.fabswingers_profile && <div>FabSwingers: <a href={r.fabswingers_profile} target="_blank" rel="noreferrer" className="text-[#D9A6F5]">{r.fabswingers_profile}</a></div>}
                        {r.swinghub_profile && <div>SwingHub: <a href={r.swinghub_profile} target="_blank" rel="noreferrer" className="text-[#D9A6F5]">{r.swinghub_profile}</a></div>}
                        {r.about && <div className="mt-2 whitespace-pre-wrap">{r.about}</div>}
                      </div>
                    )}
                  </div>
                  {tab === "pending" && (
                    <div className="flex flex-col gap-2 flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => approve(r.id)}
                        disabled={busy === r.id}
                        data-testid={`approve-${r.id}`}
                        className="inline-flex items-center gap-2 bg-[#D9A6F5] text-black hover:bg-[#E8C8FF] disabled:opacity-40 px-4 py-2 text-xs uppercase tracking-[0.22em] font-bold"
                      >
                        {busy === r.id ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                        Approve
                      </button>
                      <button
                        type="button"
                        onClick={() => reject(r.id)}
                        disabled={busy === r.id}
                        data-testid={`reject-${r.id}`}
                        className="inline-flex items-center gap-2 border border-white/15 hover:border-white/40 text-[#A3A3A3] hover:text-[#F5F5F0] disabled:opacity-40 px-4 py-2 text-xs uppercase tracking-[0.22em]"
                      >
                        <X size={12} /> Reject
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
